// Copyright (c) StrangeBee 2024

import React, { ReactElement, useContext, useEffect, useMemo } from 'react';
import { useLocalMessage } from '@Helpers/';
import {
    CaseOutput,
    UserOutput,
    CustomStatusMapType,
    CaseStatusStage,
    PermissionsEnum,
    OrganisationOutput,
    ProfileOutput,
    OrganisationLinksOutput,
} from 'thehive-sdk';
import Details from '@Molecules/Details/Details';
import { extractData } from '@Organisms/Case/extractData';
import { CaseEventList } from '@Organisms/Case/eventList';
import { useCurrentUser } from '@Queries/userQueries';
import { RouteType } from '@Types';
import { Shortcut } from '@Molecules/Shortcut/Shortcut';
import useListQuery from '@Hooks/useListQuery';
import client from '@Client';
import { DisplayMetadata } from '@Molecules/DisplayMetadata/DisplayMetadata';
import { CopyValue } from '@Atoms/CopyValue/CopyValue';
import AssigneeInfo from '@Molecules/AssigneeInfo/AssigneeInfo';
import { FormatDate } from '@Molecules/Format';
import { ICONS } from '@Constants/iconsApp';
import { useListAllFields } from '@Queries/describeQueries';
import { ScopeContext } from '@Context/ScopeProvider';
import { useStreamObserver } from '@Hooks/useStream';
import { STREAM_EVENTS } from '@Services/StreamSrv';
import { useSplitPermissions } from '@Hooks/useSplitPermissions';
import { useGenericQuery } from '@Queries/genericQueries';
import useOneQuery from '@Hooks/useOneQuery';
import { computeGetSelector } from '@Organisms/Organisation/selector';
import { computeListSelector } from '@Organisms/Profile/selector';
import { computeListSelector as computeOrgListSelector } from '@Organisms/Organisation/selector';
import { CaseChangeOwner } from '@Organisms/Case/CaseChangeOwner/CaseChangeOwner';
import { useDrawer } from '@Hooks/useDrawer';

import './Layout.scss';

interface CaseLayoutSiderProps {
    caseId: string;
    data?: CaseOutput;
    eventList: CaseEventList;
    routes: RouteType[];
}

const CaseLayoutSider = ({ caseId, data, eventList, routes }: CaseLayoutSiderProps): ReactElement => {
    const i = useLocalMessage();

    const { assignee, _createdBy, _createdAt, _updatedAt, tlp, pap, severity, status, stage, extraData, impactStatus } =
        data || {};
    const { permissions = [] } = extraData || {};

    const { visible, setVisible, data: drawerData, setData } = useDrawer<string>({});

    const { data: currentUser } = useCurrentUser();

    const { data: currentOrganisation } = useOneQuery<OrganisationOutput>({
        controller: client.OrganisationController,
        selector: computeGetSelector({ orgId: currentUser?.organisation || '' }),
        manual: true,
        fetchOnStart: !!currentUser,
    });

    const { data: profileList = [] } = useListQuery<ProfileOutput[]>({
        queryParams: {
            selector: computeListSelector(),
        },
        controller: client.ProfileController,
        manual: true,
    });

    const { data: orgList = [] } = useListQuery<OrganisationOutput[]>({
        queryParams: {
            selector: computeOrgListSelector(),
        },
        controller: client.OrganisationController,
        manual: true,
        fetchOnStart: true,
    });

    //Add current organisation to linked organisation todo: It's a ugly way to do this here
    const sharedOrganisationList = useMemo<OrganisationLinksOutput[]>(() => {
        const linkedOrganisationList = currentOrganisation?.links ?? [];
        return [
            ...linkedOrganisationList,
            {
                toOrganisation: currentOrganisation?.name,
                avatar: currentOrganisation?.avatar,
            },
        ];
    }, [currentOrganisation]);

    const [caseStatus, setCaseStatus] = React.useState<CustomStatusMapType>({});
    const { data: describeData } = useListAllFields();

    const {
        customStatus: {
            case: { list: caseStatusList },
        },
    } = useContext(ScopeContext);

    const { data: shareList = [], refetch: refetchShareList } = useGenericQuery({
        fetchCallback: () => eventList.shares.onList(caseId),
        key: ['sharings', caseId],
    });

    const { data: assignableUsers = [] } = useListQuery<UserOutput[]>({
        queryParams: {
            selector: {
                head: [{ name: 'getCase', idOrName: caseId }, { name: 'assignableUsers' }],
                name: `get-case-${caseId}-assignable-users`,
                initialFilters: [
                    {
                        field: 'locked',
                        operator: 'any',
                        values: [false],
                        type: 'boolean',
                        describeAttribute: describeData?.user.attributes.find(({ name }) => name === 'locked') as any,
                    },
                ],
                sort: ['+name'],
            },
        },
        controller: client.UserController,
        manual: true,
        fetchOnStart: true,
    });

    const hasPermissions = useSplitPermissions(permissions, [
        PermissionsEnum.MANAGE_CASE_UPDATE,
        PermissionsEnum.MANAGE_CASE_CHANGE_OWNERSHIP,
        PermissionsEnum.MANAGE_CASE_REOPEN,
    ]);

    const isOpen = stage !== CaseStatusStage.CLOSED;

    useEffect(() => {
        if (caseStatusList.length) {
            const caseStatusMap = caseStatusList.reduce((acc, curr) => {
                if (
                    isOpen &&
                    ![CaseStatusStage.NEW, CaseStatusStage.IN_PROGRESS].includes(curr.stage as CaseStatusStage)
                ) {
                    return acc;
                }
                if (!acc[curr.stage]) {
                    acc[curr.stage] = [];
                }
                acc[curr.stage].push(curr);
                return acc;
            }, {} as CustomStatusMapType);
            setCaseStatus(caseStatusMap);
        }
    }, [caseStatusList, isOpen]);

    useStreamObserver({ rootId: caseId, objectType: STREAM_EVENTS.CASE }, (data) => {
        const canRefetch = data.some(
            (audit) =>
                audit.base.action === 'update' && (audit.base.details?.taskRule || audit.base.details?.observableRule),
        );

        if (canRefetch) {
            refetchShareList();
        }
    });

    return (
        <div className="case-details">
            <div className="case-details__header">
                <DisplayMetadata
                    direction="vertical"
                    className="case-details__metadata"
                    items={[
                        {
                            icon: <ICONS.SettingIcon />,
                            label: 'id',
                            value: <CopyValue value={caseId}>{caseId}</CopyValue>,
                        },
                        {
                            icon: <ICONS.UserIcon />,
                            label: i('common.created-by'),
                            value: <AssigneeInfo assignee={_createdBy} noAvatar />,
                        },
                        {
                            icon: <ICONS.CreationDateIcon />,
                            label: i('common.created-at'),
                            value: <FormatDate date={_createdAt} />,
                        },
                        {
                            icon: <ICONS.UpdateDateIcon />,
                            label: i('common.updated-at'),
                            value: <FormatDate date={_updatedAt} />,
                            hidden: !_updatedAt,
                        },
                    ]}
                />
            </div>

            {data && (
                <Details
                    className="case-details__content"
                    data={{
                        ...data,
                        '#computeSecurity': { tlp, pap, severity },
                        '#computeOwner': !!currentOrganisation?.links?.length
                            ? {
                                  currentOrganisation: currentOrganisation?.name,
                                  owningOrganisation: extraData?.owningOrganisation,
                                  organisationList: sharedOrganisationList,
                                  setOrganisation: (value: string) => {
                                      setVisible(true);
                                      setData(value);
                                  },
                              }
                            : undefined,
                        '#computeAssignee': {
                            assignableUsers,
                            assignee,
                        },
                        '#computeStatus': {
                            status,
                            statusList:
                                !isOpen && !hasPermissions.reopenCase ? { Closed: caseStatus.Closed } : caseStatus,
                            isOpen: isOpen,
                            impactStatus,
                            isClosedAndHasPermission: !isOpen && hasPermissions.updateCase,
                        },
                        '#computeCount': {
                            observableCount: extraData?.observableStats?.total,
                            shareCount: extraData?.shareCount,
                            actionRequired: extraData?.actionRequired,
                            taskStats: extraData?.taskStats,
                            procedureCount: extraData?.procedureCount,
                        },
                        '#computeShareList': shareList.filter(
                            ({ organisationName }) =>
                                currentUser?.organisation !== organisationName &&
                                orgList.find(({ name, locked }) => {
                                    return name === organisationName && !locked;
                                }),
                        ),
                        '#computeContributors': { contributors: extraData?.contributors },
                    }}
                    extract={extractData(caseId, hasPermissions, eventList, i)}
                    forbidden={['extraData.isOwner', !sharedOrganisationList.length ? '#computeOwner' : '']}
                />
            )}

            <div className="case-details__footer">
                <Shortcut routes={routes} paramName="caseId" entityId={caseId} />
            </div>

            <CaseChangeOwner
                onFinish={(values) => eventList.onOwnerChange(values, caseId)}
                visible={visible}
                setVisible={setVisible}
                profileList={profileList}
                linkedOrgs={sharedOrganisationList}
                newOrg={drawerData}
            />
        </div>
    );
};

export default CaseLayoutSider;
